/**
 * Typefaces the document editor offers, and what each one becomes in the PDF.
 *
 * This is the module that makes "what I see is what is filed" true, and it is
 * worth being blunt about why it is not simply a list of nice fonts.
 *
 * The editor breaks pages by MEASURING text (see `layout.ts`), and the PDF
 * exporter lays the same text out with jsPDF. If the browser measures Calibri
 * and jsPDF draws Helvetica, the two disagree about where a line wraps, and the
 * document paginates differently on screen from the file that goes out — the
 * exact failure OpenDraft avoids by keeping its line counter and its exporter
 * in one place.
 *
 * So every face here declares `pdf`, the jsPDF family it is drawn with, and
 * `exact`. An `exact: true` face has the same metrics in both places — Arial
 * IS Helvetica's metric twin, Times New Roman IS Times's, Courier New IS
 * Courier's — and pagination is identical. An `exact: false` face is offered
 * because people ask for it, is approximated in the PDF by its closest core
 * family, and the editor measures using that approximation so the two still
 * AGREE, at the cost of the screen being very slightly off its own typeface.
 * Agreeing is the property worth keeping; matching a screen font exactly is not.
 *
 * Adding a truly-embedded face means shipping its TTF and registering it with
 * jsPDF's VFS — `registerEmbeddedFont` is where that goes, and until then this
 * list is honest about what it can do.
 */

export type PdfCoreFamily = 'helvetica' | 'times' | 'courier'

export interface FontFace {
  /** The name a template stores and the picker shows. */
  name: string
  /** The jsPDF family it is drawn with. */
  pdf: PdfCoreFamily
  /** Whether screen and PDF metrics are the same face. */
  exact: boolean
  /** CSS stack used for measurement AND for rendering. */
  stack: string
  category: 'Sans serif' | 'Serif' | 'Monospaced'
}

/**
 * The measurement stack deliberately leads with the PDF face's metric twin.
 * That is what keeps the on-screen wrap and the exported wrap on the same word.
 */
const HELVETICA_STACK = 'Arial, Helvetica, "Liberation Sans", sans-serif'
const TIMES_STACK = '"Times New Roman", Times, "Liberation Serif", serif'
const COURIER_STACK = '"Courier New", Courier, "Liberation Mono", monospace'

export const FONT_FACES: FontFace[] = [
  { name: 'Arial', pdf: 'helvetica', exact: true, stack: HELVETICA_STACK, category: 'Sans serif' },
  { name: 'Helvetica', pdf: 'helvetica', exact: true, stack: HELVETICA_STACK, category: 'Sans serif' },
  { name: 'Times New Roman', pdf: 'times', exact: true, stack: TIMES_STACK, category: 'Serif' },
  { name: 'Courier New', pdf: 'courier', exact: true, stack: COURIER_STACK, category: 'Monospaced' },
  // Approximated. Measured as their PDF stand-in so the two surfaces agree.
  { name: 'Calibri', pdf: 'helvetica', exact: false, stack: HELVETICA_STACK, category: 'Sans serif' },
  { name: 'Verdana', pdf: 'helvetica', exact: false, stack: HELVETICA_STACK, category: 'Sans serif' },
  { name: 'Tahoma', pdf: 'helvetica', exact: false, stack: HELVETICA_STACK, category: 'Sans serif' },
  { name: 'Segoe UI', pdf: 'helvetica', exact: false, stack: HELVETICA_STACK, category: 'Sans serif' },
  { name: 'Cambria', pdf: 'times', exact: false, stack: TIMES_STACK, category: 'Serif' },
  { name: 'Georgia', pdf: 'times', exact: false, stack: TIMES_STACK, category: 'Serif' },
  { name: 'Garamond', pdf: 'times', exact: false, stack: TIMES_STACK, category: 'Serif' },
  { name: 'Book Antiqua', pdf: 'times', exact: false, stack: TIMES_STACK, category: 'Serif' },
  { name: 'Consolas', pdf: 'courier', exact: false, stack: COURIER_STACK, category: 'Monospaced' },
]

const BY_NAME = new Map(FONT_FACES.map((f) => [f.name.toLowerCase(), f]))

export const DEFAULT_FONT = 'Arial'

/** The face a CSS generic keyword means here. */
const GENERIC: Record<string, string> = {
  'sans-serif': 'Arial',
  'ui-sans-serif': 'Arial',
  'system-ui': 'Arial',
  serif: 'Times New Roman',
  'ui-serif': 'Times New Roman',
  monospace: 'Courier New',
  'ui-monospace': 'Courier New',
}

/** What inline `code` is drawn in — the mark means monospace. */
export const CODE_FONT = 'Courier New'

function faceNamed(value: string): FontFace | undefined {
  const key = value.trim().replace(/^["']|["']$/g, '').toLowerCase()
  if (!key) return undefined
  return BY_NAME.get(key) || BY_NAME.get((GENERIC[key] || '').toLowerCase())
}

export function findFace(name: string | null | undefined): FontFace {
  const raw = String(name || '').trim()
  const named = faceNamed(raw)
  if (named) return named

  /*
   * A CSS STACK, not a face name.
   *
   * Text pasted from a word processor or a web page arrives carrying the
   * source's whole `font-family` — `"Google Sans", Arial, sans-serif` is a
   * real value out of a real document in this repository. Matching that whole
   * string against the face list never hits, so every pasted run fell to the
   * default even when the stack NAMED a face here: that one asks for Arial
   * second and sans-serif third, and both are supported. A stack led by an
   * unknown SERIF face was worse — it came out in Arial, the wrong shape
   * rather than merely the wrong face.
   *
   * Read it the way a browser does: the first entry that resolves wins,
   * generic keywords included. Measurement (`layout.measureText`) and drawing
   * (`pdfExporter.applyFont`) both come through here, so they still agree.
   */
  if (raw.includes(',')) {
    for (const part of raw.split(',')) {
      const face = faceNamed(part)
      if (face) return face
    }
  }

  // Genuinely unknown — a template written elsewhere, a face someone removed —
  // renders in the default rather than in whatever the browser guesses, so the
  // measured layout and the drawn one still describe the same page.
  return BY_NAME.get(DEFAULT_FONT.toLowerCase())!
}

/** The CSS `font-family` for a template family. Always a real stack. */
export function fontStack(name: string | null | undefined): string {
  return findFace(name).stack
}

/** The jsPDF family a template family is drawn with. */
export function pdfFamily(name: string | null | undefined): PdfCoreFamily {
  return findFace(name).pdf
}

/** True when screen and PDF are literally the same typeface. */
export function isExactFace(name: string | null | undefined): boolean {
  return findFace(name).exact
}

export function fontsByCategory(): Array<{ category: string; faces: FontFace[] }> {
  const groups = new Map<string, FontFace[]>()
  for (const face of FONT_FACES) {
    const list = groups.get(face.category) || []
    list.push(face)
    groups.set(face.category, list)
  }
  return [...groups.entries()].map(([category, faces]) => ({ category, faces }))
}
